import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
export const API_BASE = `${BACKEND_URL}/api`;

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export const setAccessToken = (t: string | null): void => { accessToken = t; };
export const getAccessToken = (): string | null => accessToken;
export const setOnUnauthorized = (fn: () => void): void => { onUnauthorized = fn; };

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  if (accessToken) {
    cfg.headers = cfg.headers ?? {};
    (cfg.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }
  return cfg;
});

let refreshing: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
      const tok = r.data?.access_token as string;
      if (tok) { setAccessToken(tok); return tok; }
      return null;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config;
    if (err?.response?.status === 401 && cfg && !cfg._retry) {
      cfg._retry = true;
      const tok = await tryRefresh();
      if (tok) {
        cfg.headers = cfg.headers ?? {};
        cfg.headers['Authorization'] = `Bearer ${tok}`;
        return api.request(cfg);
      }
      if (onUnauthorized) onUnauthorized();
    }
    // Tag 403s so the ErrorBoundary/CRA dev overlay treats them as
    // handled — a 403 is a *permission* signal, not a runtime bug.
    // Individual callers can still inspect err.response.status and
    // decide whether to surface a toast.
    if (err?.response?.status === 403) {
      err.isHandled403 = true;
    }
    return Promise.reject(err);
  },
);
